// ══════════════════════════════════════════════════════════════════════════
//  [WPM1 2026-08-10] MODEL CONFIG — one model string, two providers
// ══════════════════════════════════════════════════════════════════════════
//  Until now the model was a literal inside each of NINE fetch bodies (seven in
//  the browser, ADVISORY_MODEL in the server runner, plus the Fable reviewer
//  this package adds). A model change meant editing every one of them and
//  hoping the grep found them all. This module is the single place.
//
//  IDENTITY IS `provider:model` (Blake's 2026-08-06 ruling — the head-to-head
//  arm). [WPM3 2026-10-22] `openai:gpt-6.1-sol` is the browser default. A bare
//  string with no colon is read as Anthropic, so anything that predates this
//  file still means what it used to mean.
//
//  WHY THE SWAP IS SAFE (verified against Anthropic's live docs 2026-08-10):
//  claude-opus-5 is a drop-in successor to claude-opus-4-8 at IDENTICAL pricing
//  ($5/$25 per MTok), same 1M context, same 128k max output. Our calls already
//  send `thinking: {type:"adaptive"}`, which is valid and equivalent on Opus 5
//  (where thinking is additionally on by default). We send no sampling
//  parameters and no assistant prefills — the two things that hard-error on the
//  5 generation. THE SWAP IS THE STRING.
//
//  ROLLBACK is one const: set AI_MODEL_DEFAULT back to AI_MODEL_ROLLBACK.
//  claude-opus-4-8 is still served (Anthropic lists it as legacy-but-active, NOT
//  deprecated), so the rollback arm stays valid indefinitely.
//
//  Dependency-free by the same rule as the rest of src/pipeline/: the browser,
//  api/_advisory-pipeline.js and api/selftest.js all import it.
// ══════════════════════════════════════════════════════════════════════════

// ── The model ──────────────────────────────────────────────────────────────
// [WPM1-R2 — Blake's ruling 2026-08-11, from the pre-merge model race on Mini DTLA]
// "To me, the wrong includes are the single most important metric … I like the GPT Sol as the
// model and Fable, obviously, as the reviewer." gpt-5.6-sol posted the fewest wrong-Includes
// with the reviewer on (2, vs opus-4-8's 10) — findings-m1ab-model-race-20260811 (local).
// [WPM3 2026-10-22] Blake reseated the browser default to GPT-6.1 Sol; the ADVISORY lane
// deliberately stays on the rollback model (see below).
export const AI_MODEL_DEFAULT  = "openai:gpt-6.1-sol";
export const AI_MODEL_ROLLBACK = "anthropic:claude-opus-4-8";   // kept valid — the one-const revert
// The Anthropic flagship — the standing comparison arm now that the default is OpenAI.
export const ANTHROPIC_FLAGSHIP = "anthropic:claude-opus-5";

// [WPM1] 16000 → 32000. On the 5 generation `max_tokens` caps thinking AND text
// together, and Opus 5 may think longer than 4.8 did; a run that thinks past the
// old ceiling would truncate mid-JSON and fail the parse. Headroom is free
// unless used — we are billed on tokens produced, not on the cap.
export const AI_MAX_TOKENS = 32000;

// [WPM1-B] The final-review pass. Anthropic-only regardless of which model won
// the main-classification arm (spec §A2.5) — this is a second opinion from the
// most capable model available, not a second arm.
//
// [WPM3 2026-10-22] Opus 5.5 accepts adaptive thinking and runs this seat at Blake's
// chosen high effort. The effort is passed only by the final-review call.
//
// PINNED IN ALL ARMS — LAW, NOT A DEFAULT (Blake's ruling 2026-08-10). The reviewer NEVER
// switches with the main-model arm: the whole value of a second opinion is that it comes from a
// different vantage point than the model being reviewed, so an `ai-model` arm that dragged the
// reviewer along with it would quietly turn the second opinion into the same opinion twice.
// `fableReviewModelId()` is the ONLY way this pass gets a model, and it takes no argument.
export const FABLE_REVIEW_MODEL = "anthropic:claude-opus-5-5";
export const FABLE_REVIEW_EFFORT = "high";
export function fableReviewModelId() { return FABLE_REVIEW_MODEL; }

// [WPM1-B] The routing threshold for an auto-decided line. A flag BELOW it is still recorded and
// still counted — it just doesn't buy review labor (Blake: "a sub-threshold flag must not vanish
// silently … so the threshold's cost is measurable"). A flag with no confidence at all routes:
// an unquantified disagreement is still a disagreement, and routing is the safe direction.
export const FABLE_FLAG_MIN_CONFIDENCE = 60;
export function fableFlagRoutes(flag, confidence) {
  if (flag !== true) return false;
  return confidence == null || Number(confidence) >= FABLE_FLAG_MIN_CONFIDENCE;
}
// $10 / $50 per MTok (live docs 2026-08-10) — the run summary prices the pass so
// "the $5 second opinion" is a measured number rather than a figure of speech.
export const FABLE_REVIEW_PRICE = { inPerMTok: 10, outPerMTok: 50 };

// [WP33 2026-08-11] Standard API token prices, USD per million tokens. Verified
// 2026-08-11 against the live provider docs (and independently re-verified by the reviewer):
// https://developers.openai.com/api/docs/models/gpt-5.6-sol
// https://developers.openai.com/api/docs/guides/prompt-caching
// https://platform.claude.com/docs/en/about-claude/pricing
// https://platform.claude.com/docs/en/about-claude/models/introducing-claude-fable-5-and-claude-mythos-5
// Unknown models are deliberately absent: callers must show "$ unpriced", never borrow a
// nearby model's rate. The browser proxies use standard/global routing (no priority, batch,
// data-residency, or US-only multiplier).
export const AI_PRICE_TABLE = Object.freeze({
  "openai:gpt-5.6-sol": Object.freeze({ input: 5, cacheWrite: 6.25, cacheRead: 0.5, output: 30 }),
  "openai:gpt-6.1-sol": Object.freeze({ input: 2, cacheWrite: 2.5, cacheRead: 0.1, output: 10 }),
  "anthropic:claude-opus-5": Object.freeze({ input: 5, cacheWrite: 6.25, cacheRead: 0.5, output: 25 }),
  "anthropic:claude-opus-4-8": Object.freeze({ input: 5, cacheWrite: 6.25, cacheRead: 0.5, output: 25 }),
  "anthropic:claude-fable-5": Object.freeze({ input: 10, cacheWrite: 12.5, cacheRead: 1, output: 50 }),
  "anthropic:claude-opus-5-5": Object.freeze({ input: 4, cacheWrite: 5, cacheRead: 0.2, output: 20 }),
});

export const OPENAI_LONG_CONTEXT_INPUT_TOKENS = 272000;

// Normalize the response shape without inventing omitted fields. Chat Completions uses
// prompt_tokens(_details); Responses uses input_tokens(_details). Both include cache tokens in
// the top-level input count, so subtract the read/write buckets to obtain ordinary input.
export function normalizeAiUsage(provider, data) {
  const u = data?.usage;
  if (!u || typeof u !== "object") return null;
  if (provider === "openai") {
    const details = u.prompt_tokens_details || u.input_tokens_details || {};
    const totalInput = Number(u.prompt_tokens ?? u.input_tokens);
    const outputTokens = Number(u.completion_tokens ?? u.output_tokens);
    if (!Number.isFinite(totalInput) || !Number.isFinite(outputTokens)) return null;
    const cacheReadTokens = Number(details.cached_tokens || 0);
    const cacheWriteTokens = Number(details.cache_write_tokens || 0);
    return {
      inputTokens: Math.max(0, totalInput - cacheReadTokens - cacheWriteTokens),
      cacheWriteTokens: Math.max(0, cacheWriteTokens), cacheReadTokens: Math.max(0, cacheReadTokens),
      outputTokens: Math.max(0, outputTokens), totalInputTokens: Math.max(0, totalInput),
    };
  }
  const inputTokens = Number(u.input_tokens);
  const outputTokens = Number(u.output_tokens);
  if (!Number.isFinite(inputTokens) || !Number.isFinite(outputTokens)) return null;
  const cacheWriteTokens = Number(u.cache_creation_input_tokens || 0);
  const cacheReadTokens = Number(u.cache_read_input_tokens || 0);
  return {
    inputTokens: Math.max(0, inputTokens), cacheWriteTokens: Math.max(0, cacheWriteTokens),
    cacheReadTokens: Math.max(0, cacheReadTokens), outputTokens: Math.max(0, outputTokens),
    totalInputTokens: Math.max(0, inputTokens + cacheWriteTokens + cacheReadTokens),
  };
}

// Price ONE call so OpenAI's long-context boundary is applied before calls are accumulated.
export function priceAiCall(modelId, usage) {
  const price = AI_PRICE_TABLE[modelId];
  if (!usage) return { priced: !!price, costUsd: null, longContext: false };
  if (!price) return { priced: false, costUsd: null, longContext: false };
  const longContext = (modelId === "openai:gpt-5.6-sol" || modelId === "openai:gpt-6.1-sol") && usage.totalInputTokens > OPENAI_LONG_CONTEXT_INPUT_TOKENS;
  const inputMultiplier = longContext ? 2 : 1;
  const outputMultiplier = longContext ? 1.5 : 1;
  const inputCost = (usage.inputTokens * price.input + usage.cacheWriteTokens * price.cacheWrite + usage.cacheReadTokens * price.cacheRead) * inputMultiplier;
  const outputCost = usage.outputTokens * price.output * outputMultiplier;
  return { priced: true, costUsd: (inputCost + outputCost) / 1_000_000, longContext };
}

export function createUsageAccumulator() {
  const calls = [];
  return {
    add({ stage, provider, model, data }) {
      const modelId = `${provider}:${model}`;
      const usage = normalizeAiUsage(provider, data);
      calls.push({ stage, model: modelId, usage, ...priceAiCall(modelId, usage) });
    },
    snapshot() {
      const make = () => ({ calls: 0, unknownCalls: 0, inputTokens: 0, cacheWriteTokens: 0, cacheReadTokens: 0, outputTokens: 0, costUsd: 0, unpriced: false, longContextCalls: 0, models: [] });
      const perStage = {}; const totals = make();
      for (const call of calls) {
        const stage = perStage[call.stage] ||= make();
        for (const bucket of [stage, totals]) {
          bucket.calls++;
          if (!bucket.models.includes(call.model)) bucket.models.push(call.model);
          if (!call.usage) bucket.unknownCalls++;
          else {
            bucket.inputTokens += call.usage.inputTokens;
            bucket.cacheWriteTokens += call.usage.cacheWriteTokens;
            bucket.cacheReadTokens += call.usage.cacheReadTokens;
            bucket.outputTokens += call.usage.outputTokens;
          }
          if (!call.priced) bucket.unpriced = true;
          if (call.costUsd != null) bucket.costUsd += call.costUsd;
          if (call.longContext) bucket.longContextCalls++;
        }
      }
      return { perStage, totals };
    },
  };
}

// [WP33-R2 2026-08-11] Retained for the WPM1 pin tests ONLY — the live Fable call site now
// meters through createUsageAccumulator (one mechanism). Cache creation is billed at the
// verified 1.25x write rate here too, so the pinned law and the live law agree.
export function estimateCost(usage, price = FABLE_REVIEW_PRICE) {
  const u = usage || {};
  const inTok = Number(u.input_tokens || 0);
  const writeTok = Number(u.cache_creation_input_tokens || 0);
  const cacheTok = Number(u.cache_read_input_tokens || 0);
  const outTok = Number(u.output_tokens || 0);
  const dollars = (inTok * price.inPerMTok + writeTok * price.inPerMTok * 1.25 + cacheTok * price.inPerMTok * 0.1 + outTok * price.outPerMTok) / 1_000_000;
  return Number.isFinite(dollars) ? dollars : 0;
}

// Both providers' usage blocks, normalized to the Anthropic field names the
// estimator reads.
export function aiUsage(provider, data) {
  if (provider === "openai") {
    const u = data?.usage || {};
    const details = u.prompt_tokens_details || u.input_tokens_details || {};
    return {
      input_tokens: Number(u.prompt_tokens ?? u.input_tokens ?? 0) - Number(details.cached_tokens || 0) - Number(details.cache_write_tokens || 0),
      cache_creation_input_tokens: Number(details.cache_write_tokens || 0),
      cache_read_input_tokens: Number(details.cached_tokens || 0),
      output_tokens: Number(u.completion_tokens ?? u.output_tokens ?? 0),
    };
  }
  return data?.usage || {};
}

// [WPM3 2026-10-22] The OpenAI health-probe arm follows the browser's seated model while
// remaining its own OpenAI-only constant (a rollback can put the browser back on Anthropic).
export const OPENAI_ARM_MODEL = "openai:gpt-6.1-sol";

export const AI_PROVIDERS = ["anthropic", "openai"];

// The browser override key + the server env override. Same pattern as
// 'brain-rag' / 'db1-exact': a localStorage arm switch that fails safe to the
// default when storage is unreadable (headless, private mode, a hostile value).
export const AI_MODEL_STORAGE_KEY = "ai-model";
export const AI_MODEL_ENV_KEY     = "ADVISORY_MODEL_OVERRIDE";

// ── Identity ───────────────────────────────────────────────────────────────
// Pure. Never throws: a malformed or unknown-provider id resolves to the
// default, because a typo in a localStorage arm switch must not brick the tool
// mid-file. `aiModelId()` is where that substitution becomes VISIBLE (warn).
export function parseModelId(id) {
  const s = String(id ?? "").trim();
  if (!s) return parseModelId(AI_MODEL_DEFAULT);
  const cut = s.indexOf(":");
  if (cut < 0) return { provider: "anthropic", model: s };   // bare = Anthropic (back-compat)
  const provider = s.slice(0, cut).trim().toLowerCase();
  const model = s.slice(cut + 1).trim();
  if (!model || !AI_PROVIDERS.includes(provider)) return parseModelId(AI_MODEL_DEFAULT);
  return { provider, model };
}

export function isValidModelId(id) {
  const s = String(id ?? "").trim();
  if (!s) return false;
  const cut = s.indexOf(":");
  if (cut < 0) return true;
  const provider = s.slice(0, cut).trim().toLowerCase();
  return AI_PROVIDERS.includes(provider) && s.slice(cut + 1).trim().length > 0;
}

// The BROWSER's answer to "which model is this run using?". Read once per run by
// the caller and passed down, so every stage of one run uses one model even if
// the tab's localStorage is edited mid-run.
export function aiModelId() {
  try {
    if (typeof localStorage === "undefined") return AI_MODEL_DEFAULT;
    const raw = localStorage.getItem(AI_MODEL_STORAGE_KEY);
    if (!raw) return AI_MODEL_DEFAULT;
    if (!isValidModelId(raw)) {
      console.warn(`[WPM1] ignoring unrecognized '${AI_MODEL_STORAGE_KEY}' = ${JSON.stringify(raw)} — using ${AI_MODEL_DEFAULT}`);
      return AI_MODEL_DEFAULT;
    }
    return String(raw).trim();
  } catch { return AI_MODEL_DEFAULT; }
}

// The SERVER's answer (advisory runner). env → ADVISORY default; same validation.
// [WPM1-R2 — Blake's ruling 2026-08-11] The advisory lane does NOT follow the browser default.
// In the quick-verdict lane the error asymmetry INVERTS: an advisory Exclude is FINAL (no human
// re-screen), an Include is re-screened downstream. The model race showed the 5-generation
// models confidently Exclude the time-transfer line class that opus-4-8 routes to review — so
// until batch 3.8's reference-line guard lands, the advisory seat stays on the model with the
// sharpest Exclude column. Encoded here as a code default (not an env-var memory test);
// ADVISORY_MODEL_OVERRIDE still wins when set.
export const ADVISORY_MODEL_DEFAULT = AI_MODEL_ROLLBACK;  // anthropic:claude-opus-4-8
export function advisoryModelId(env) {
  const raw = (env || (typeof process !== "undefined" ? process.env : null) || {})[AI_MODEL_ENV_KEY];
  if (!raw) return ADVISORY_MODEL_DEFAULT;
  if (!isValidModelId(raw)) {
    console.warn(`[WPM1] ignoring unrecognized ${AI_MODEL_ENV_KEY} — using ${ADVISORY_MODEL_DEFAULT}`);
    return ADVISORY_MODEL_DEFAULT;
  }
  return String(raw).trim();
}

// ── Request construction ───────────────────────────────────────────────────
// Both providers receive the SAME system text and the SAME user text. Only the
// envelope differs. That is deliberate: the arm measures "our whole system with
// that model dropped in", so anything that changed the prompt per provider would
// measure the prompt instead of the model.
export function aiEndpoint(provider) {
  return provider === "openai" ? "/api/openai" : "/api/claude";
}

export function buildAiBody({ provider, model, system, userText, maxTokens = AI_MAX_TOKENS, effort }) {
  if (provider === "openai") {
    return {
      model,
      // GPT-5.x reasoning models reject `max_tokens`; the reasoning-inclusive cap
      // is `max_completion_tokens` (OpenAI live docs 2026-08-10). Same number as
      // the Anthropic side, and it likewise covers reasoning + visible output.
      max_completion_tokens: maxTokens,
      // No `response_format`. Every one of our prompts returns a JSON ARRAY, and
      // OpenAI's json_object mode requires a top-level object — it would force a
      // per-call-site schema rewrite and change the prompts for one arm only.
      // Prompt-driven JSON keeps the two arms textually identical; the shared
      // fence-stripping parse handles both. [BUILDER DEFAULT — flagged]
      messages: [{ role: "system", content: system }, { role: "user", content: userText }],
    };
  }
  return {
    model,
    max_tokens: maxTokens,
    thinking: { type: "adaptive" },
    ...(effort == null ? {} : { output_config: { effort } }),
    system: [{ type: "text", text: system, cache_control: { type: "ephemeral" } }],
    messages: [{ role: "user", content: userText }],
  };
}

// ── Response reading ───────────────────────────────────────────────────────
// A refusal is NOT an answer. Both providers can decline a request while
// returning HTTP 200 (Anthropic: stop_reason "refusal"; OpenAI:
// message.refusal / finish_reason "content_filter"). Parsing the content of a
// refusal is how a batch of warranty lines silently becomes garbage — so every
// read goes through here and a refusal is raised as a failed batch, which lands
// in the existing fail-open/Human-Review paths. Near-zero likelihood on RO text;
// the guard is one conditional and it can only ever route to a human.
export class AiRefusalError extends Error {
  constructor(provider, detail) {
    super(`${provider} refused this batch${detail ? `: ${detail}` : ""}`);
    this.name = "AiRefusalError";
    this.code = "ai_refusal";
    this.provider = provider;
    this.detail = detail || "";
  }
}

export function aiRefusal(provider, data) {
  if (provider === "openai") {
    const choice = data?.choices?.[0];
    if (choice?.message?.refusal) return String(choice.message.refusal);
    if (choice?.finish_reason === "content_filter") return "content_filter";
    return null;
  }
  if (data?.stop_reason === "refusal") {
    return data?.stop_details?.explanation || data?.stop_details?.category || "refusal";
  }
  return null;
}

// The raw assistant text. Anthropic: the first text block (adaptive thinking
// lives in separate `thinking` blocks, which this has always ignored).
export function aiText(provider, data) {
  if (provider === "openai") return data?.choices?.[0]?.message?.content || "";
  return (data?.content || []).find(b => b.type === "text")?.text || "";
}

// The one extraction path: refuse → throw; otherwise strip any fence and parse.
// Callers keep their own shape expectations (array vs object).
export function parseAiJson(provider, data, fallback = "[]") {
  const refusal = aiRefusal(provider, data);
  if (refusal) throw new AiRefusalError(provider, refusal);
  const raw = aiText(provider, data) || fallback;
  return JSON.parse(String(raw).replace(/```json|```/g, "").trim());
}
